import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Loader2, Check } from "lucide-react";

export default function Committees() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", type: "committee" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/committees");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (c) => {
    try {
      await api.post(`/committees/${c.id}/${c.is_member ? "leave" : "join"}`);
      toast.success(c.is_member ? `Left ${c.name}` : `Joined ${c.name}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const propose = async () => {
    if (!form.name || !form.description) { toast.error("Name and description are required."); return; }
    setSaving(true);
    try {
      await api.post("/committees", form);
      toast.success("Your proposal has been established.");
      setOpen(false);
      setForm({ name: "", description: "", type: "committee" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="tribal-stripe h-1.5 w-full" />

      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-2">Self-Determination in Action</div>
            <h1 className="font-serif-display text-5xl font-medium tracking-tight text-[#F5F3EF]">Committees & Programs</h1>
            <p className="text-[#A8A29E] mt-3 max-w-xl">Join the working bodies of the Nation, or propose a new committee or program for the people to build together.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white" data-testid="propose-committee-btn">
                <Plus className="w-4 h-4 mr-2" /> Propose New
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-[#141414] border-[#D4AF37]/30 text-[#F5F3EF]">
              <DialogHeader><DialogTitle className="font-serif-display text-2xl text-[#F5F3EF]">Propose a Committee or Program</DialogTitle></DialogHeader>
              <div className="space-y-5 py-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#A8A29E]">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="rounded-none mt-1 bg-transparent border-[#F5F3EF]/20 text-[#F5F3EF]" data-testid="propose-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="committee">Committee</SelectItem>
                      <SelectItem value="program">Program</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase tracking-wider text-[#A8A29E]">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1 bg-transparent border-[#F5F3EF]/20 text-[#F5F3EF]" data-testid="propose-name-input" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-[#A8A29E]">Description / Purpose</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none mt-1 bg-transparent border-[#F5F3EF]/20 text-[#F5F3EF]" data-testid="propose-desc-input" /></div>
              </div>
              <DialogFooter>
                <Button onClick={propose} disabled={saving} className="rounded-none bg-[#C8102E] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A]" data-testid="propose-submit-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Establish"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((c) => (
              <div key={c.id} className="bg-[#141414] border border-[#D4AF37]/30 p-7 flex flex-col hover:border-[#D4AF37] transition-colors" data-testid={`committee-card-${c.id}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className={`text-[10px] tracking-[0.2em] uppercase font-semibold px-2 py-1 ${c.type === "program" ? "bg-[#5DBB7A]/15 text-[#5DBB7A]" : "bg-[#C8102E]/15 text-[#F5A3A3]"}`}>{c.type}</span>
                  <span className="flex items-center gap-1 text-xs text-[#A8A29E]"><Users className="w-3.5 h-3.5" /> {c.member_count}</span>
                </div>
                <h3 className="font-serif-display text-2xl mb-2 text-[#F5F3EF]">{c.name}</h3>
                <p className="text-sm text-[#A8A29E] leading-relaxed flex-1">{c.description}</p>
                <p className="text-[11px] text-[#A8A29E]/70 mt-4">Proposed by {c.proposed_by}</p>
                <Button onClick={() => toggle(c)}
                  className={`mt-4 rounded-none ${c.is_member ? "bg-transparent border border-[#5DBB7A] text-[#5DBB7A] hover:bg-[#5DBB7A] hover:text-[#0A0A0A]" : "bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C8102E] hover:text-white"}`}
                  data-testid={`committee-toggle-${c.id}`}>
                  {c.is_member ? <><Check className="w-4 h-4 mr-2" /> Member</> : "Join"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
